/**
 * ExamLibraryPage.tsx (THPT App)
 * Comprehensive exam library with filtering, payment integration, and exam sections
 * Adapted from student app with THPT theme and filtered for THPT exams only
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Avatar,
  AvatarFallback,
} from '@vsat/shared/components';
import { apiService } from '@vsat/shared/lib/api';
import { useAuthStore } from '@vsat/shared/store/authStore';
import {
  AlertCircle,
  BookOpen,
  ChevronDown,
  Clock,
  Crown,
  Gift,
  Loader2,
  Lock,
  Play,
  Search,
  Sparkles,
  Star,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

import { UnifiedHeader } from '../components/UnifiedHeader';
import { UnifiedFooter } from '../components/UnifiedFooter';
import { SEOHead } from '../components/SEOHead';
import { StructuredData } from '../components/StructuredData';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { getCanonicalUrl, getSiteUrl } from '../utils/siteConfig';
import { ModernPaymentDialog } from '../components/payment/ModernPaymentDialog';
import { ExamStartConfirmation } from '../components/exam/ExamStartConfirmation';
import { useExams } from '../hooks/useExams';
import type { ExamInfo } from '../types/examTypes';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, BookOpen as BookOpenIcon } from 'lucide-react';
import { LiveActivityWidget } from '@vsat/shared/components';
import {
  trackExamSearch,
  trackExamFilter,
  trackExamStart,
  trackExamPurchase,
  initScrollDepthTracking,
} from '../telemetry/googleAnalytics';

const PAGE_TITLE = 'Kho đề thi THPT';
const PAGE_DESCRIPTION = 'Luyện tập với hàng trăm đề thi thử THPT';

interface ExamSectionProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  badge: React.ReactNode;
  exams: ExamInfo[];
  onExamSelect: (exam: ExamInfo) => void;
  showAll?: boolean;
  onToggleShowAll?: () => void;
  isPaid?: (examId: string) => boolean;
  isCheckingPayment?: (examId: string) => boolean;
  highlightDescription?: string;
}

function ExamSection({
  title,
  description,
  icon,
  badge,
  exams,
  onExamSelect,
  showAll = false,
  onToggleShowAll,
  isPaid,
  isCheckingPayment,
  highlightDescription,
}: ExamSectionProps) {
  const displayedExams = showAll ? exams : exams.slice(0, 6);
  const hasMore = exams.length > 6;

  return (
    <div className='mb-12'>
      <div className='mb-6'>
        <div className='flex items-center gap-3 mb-2'>
          {icon}
          <h2 className='text-2xl font-bold text-foreground dark:text-white'>{title}</h2>
          {badge}
        </div>
        <p className='text-sm text-muted-foreground ml-9'>{description}</p>
        {highlightDescription && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className='ml-9 mt-3 p-4 sm:p-5 bg-gradient-to-r from-primary/15 via-primary/8 to-primary/15 dark:from-primary/25 dark:via-primary/15 dark:to-primary/25 border-2 border-primary/50 dark:border-primary/60 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/70 dark:hover:border-primary/80'
          >
            <div className='flex items-start gap-3'>
              <div className='flex-shrink-0 mt-0.5'>
                <Sparkles className='w-5 h-5 sm:w-6 sm:h-6 text-primary animate-pulse' />
              </div>
              <p className='text-base sm:text-lg font-bold text-primary dark:text-primary-foreground leading-relaxed'>
                {highlightDescription}
              </p>
            </div>
          </motion.div>
        )}
      </div>

      <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
        {displayedExams.map((exam, index) => {
          const paid = exam.hasActivePayment === true || isPaid?.(exam.id) === true;
          const checking = isCheckingPayment?.(exam.id) ?? false;
          return (
            <ExamCard
              key={exam.id || index}
              exam={exam}
              index={index}
              onSelect={onExamSelect}
              isPaid={paid}
              isCheckingPayment={checking}
              hasAiGrading={exam.hasAiGrading ?? false}
            />
          );
        })}
      </div>

      {hasMore && (
        <div className='mt-6 text-center'>
          <Button
            variant='outline'
            onClick={onToggleShowAll}
            className='flex items-center gap-2 mx-auto'
          >
            {showAll ? 'Thu gọn' : `Xem thêm (${exams.length - 6})`}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${showAll ? 'rotate-180' : ''}`}
            />
          </Button>
        </div>
      )}
    </div>
  );
}

interface ExamCardProps {
  exam: ExamInfo;
  index: number;
  onSelect: (exam: ExamInfo) => void;
  isPaid?: boolean;
  isCheckingPayment?: boolean;
  hasAiGrading?: boolean;
}

function ExamCard({
  exam,
  index,
  onSelect,
  isPaid = false,
  isCheckingPayment = false,
  hasAiGrading = false,
}: ExamCardProps) {
  const isPremium =
    exam.category === 'PREMIUM' ||
    (exam.category !== 'FREE' &&
      (exam.price > 0 || exam.type === 'dynamic' || exam.type === 'static'));
  const isExamReady = exam.status === 'ACTIVE';

  const getSubjectColor = (subject: string) => {
    const subjectLower = subject.toLowerCase();
    const colorMap: Record<string, { bg: string; text: string; border: string; hover: string }> = {
      toán: {
        bg: 'bg-blue-500',
        text: 'text-white',
        border: 'border-blue-600',
        hover: 'hover:bg-blue-600',
      },
      'toán học': {
        bg: 'bg-blue-500',
        text: 'text-white',
        border: 'border-blue-600',
        hover: 'hover:bg-blue-600',
      },
      'tiếng anh': {
        bg: 'bg-emerald-500',
        text: 'text-white',
        border: 'border-emerald-600',
        hover: 'hover:bg-emerald-600',
      },
      'ngữ văn': {
        bg: 'bg-purple-500',
        text: 'text-white',
        border: 'border-purple-600',
        hover: 'hover:bg-purple-600',
      },
      'Vật lí': {
        bg: 'bg-orange-500',
        text: 'text-white',
        border: 'border-orange-600',
        hover: 'hover:bg-orange-600',
      },
      'hóa học': {
        bg: 'bg-pink-500',
        text: 'text-white',
        border: 'border-pink-600',
        hover: 'hover:bg-pink-600',
      },
      'sinh học': {
        bg: 'bg-green-600',
        text: 'text-white',
        border: 'border-green-700',
        hover: 'hover:bg-green-700',
      },
      'lịch sử': {
        bg: 'bg-amber-500',
        text: 'text-white',
        border: 'border-amber-600',
        hover: 'hover:bg-amber-600',
      },
      'địa lý': {
        bg: 'bg-teal-500',
        text: 'text-white',
        border: 'border-teal-600',
        hover: 'hover:bg-teal-600',
      },
    };
    return (
      colorMap[subjectLower] || {
        bg: 'bg-gray-500',
        text: 'text-white',
        border: 'border-gray-600',
        hover: 'hover:bg-gray-600',
      }
    );
  };

  const subjectColors = getSubjectColor(exam.subject);

  return (
    <motion.div
      id={`exam-${exam.id || index}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className='h-full'
    >
      <Card
        className={`group relative overflow-visible backdrop-blur-sm shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 rounded-2xl flex flex-col cursor-pointer h-full ${
          isPremium
            ? 'border-2 border-primary/40 bg-gradient-to-br from-primary/5 via-card to-accent/5 dark:from-primary/10 dark:via-gray-900 dark:to-primary/5 hover:border-primary/60 hover:shadow-primary/20'
            : 'border border-border bg-card dark:bg-gray-900 hover:border-primary/30 hover:shadow-primary/10'
        }`}
        onClick={() => onSelect(exam)}
      >
        {isPremium && (
          <div className='absolute top-0 right-0 z-50 overflow-hidden w-20 h-20'>
            <div className='absolute top-3 -right-6 w-24 text-center py-1 text-xs font-bold text-white transform rotate-45 shadow-lg bg-gradient-to-r from-primary to-primary/80'>
              PREMIUM
            </div>
          </div>
        )}

        <CardHeader className={`pb-3 relative z-10 flex-shrink-0 ${isPremium ? 'pr-20' : ''}`}>
          <div className='mb-3 flex items-center justify-between gap-2'>
            <Badge
              className={`${subjectColors.bg} ${subjectColors.text} ${subjectColors.hover} border ${subjectColors.border} font-semibold text-sm px-3 py-1 shadow-sm transition-colors duration-300`}
            >
              {exam.subject}
            </Badge>
            <div className='flex justify-end items-center gap-2'>
              {hasAiGrading && (
                <Badge className='bg-blue-50 text-blue-600 border border-blue-300 text-xs px-2 py-0.5 dark:bg-blue-950/30 dark:border-blue-700 dark:text-blue-400'>
                  Chấm AI
                </Badge>
              )}
              {(exam.isFeatured || exam.isNew || exam.type === 'dynamic') && (
                <>
                  {exam.type === 'dynamic' && (
                    <Badge className='bg-purple-500 text-white text-xs px-2 py-0.5 border border-purple-600'>
                      <Zap className='mr-1 h-3 w-3' />
                      Động
                    </Badge>
                  )}
                  {exam.isFeatured && (
                    <Badge className='bg-warning text-primary-foreground text-xs px-2 py-0.5'>
                      <Sparkles className='mr-1 h-3 w-3' />
                      Nổi bật
                    </Badge>
                  )}
                  {exam.isNew && (
                    <Badge className='bg-blue-500 text-white text-xs px-2 py-0.5 border border-blue-600'>
                      Mới
                    </Badge>
                  )}
                </>
              )}
            </div>
          </div>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <h3 className='line-clamp-2 text-xl font-bold text-foreground dark:text-white group-hover:text-primary transition-colors duration-300 pr-4 leading-tight cursor-pointer'>
                  {exam.title.normalize('NFD')}
                </h3>
              </TooltipTrigger>
              <TooltipContent
                side='top'
                className='max-w-md p-3 bg-card border-2 border-primary/20 shadow-xl'
              >
                <p className='text-sm font-semibold text-foreground leading-relaxed'>
                  {exam.title.normalize('NFD')}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardHeader>

        <CardContent className='flex flex-col flex-1 p-6 pt-0 justify-between'>
          <div className='flex flex-col space-y-3'>
            {exam.type === 'static' && exam.teacher && (
              <div className='rounded-lg bg-gradient-to-br from-primary/10 to-primary/20 dark:from-primary/20 dark:to-primary/30 backdrop-blur-sm border border-primary/30 dark:border-primary/40 p-3 flex-shrink-0'>
                <div className='flex items-center gap-3'>
                  <Avatar className='h-8 w-8 flex-shrink-0'>
                    <AvatarFallback className='bg-primary/20 text-primary text-xs'>
                      {exam.teacher.name
                        .split(' ')
                        .map(n => n[0])
                        .join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium text-primary truncate'>{exam.teacher.name}</p>
                    <p className='text-xs text-primary truncate'>
                      {exam.teacher.totalStudents.toLocaleString()} học sinh
                    </p>
                  </div>
                  <div className='flex items-center gap-1 text-xs text-primary'>
                    <Star className='h-3 w-3 fill-yellow-400 text-yellow-400' />
                    {exam.teacher.rating}
                  </div>
                </div>
              </div>
            )}

            {exam.type === 'dynamic' && exam.questionBankSize && (
              <div className='rounded-lg bg-gradient-to-br from-primary/10 to-primary/20 dark:from-primary/20 dark:to-primary/30 backdrop-blur-sm border border-primary/30 dark:border-primary/40 p-3 flex-shrink-0'>
                <div className='flex items-center gap-2 text-sm text-primary'>
                  <Zap className='h-4 w-4' />
                  <span className='font-medium'>Đề tự động sinh</span>
                </div>
                <p className='text-xs text-primary mt-1'>
                  {exam.questionBankSize}+ câu hỏi trong ngân hàng
                </p>
              </div>
            )}

            <div className='flex flex-wrap gap-1 flex-shrink-0'>
              {exam.topics?.slice(0, 3).map(topic => (
                <Badge key={topic} variant='outline' className='text-xs'>
                  {topic}
                </Badge>
              ))}
              {exam.topics && exam.topics.length > 3 && (
                <Badge variant='outline' className='text-xs'>
                  +{exam.topics.length - 3}
                </Badge>
              )}
            </div>

            <div className='grid grid-cols-2 gap-3 text-sm text-muted-foreground flex-shrink-0'>
              <div className='flex items-center'>
                <Clock className='mr-1 h-4 w-4' />
                {exam.duration} phút
              </div>
              <div className='flex items-center'>
                <BookOpen className='mr-1 h-4 w-4' />
                {exam.totalQuestions} câu
              </div>
              {exam.rating > 0 ? (
                <div className='flex items-center'>
                  <Star className='mr-1 h-4 w-4 fill-yellow-400 text-yellow-400' />
                  {exam.rating}
                </div>
              ) : (
                <div className='flex items-center text-muted-foreground'>
                  <Star className='mr-1 h-4 w-4 text-muted-foreground' />
                  Chưa có
                </div>
              )}
              <div className='flex items-center text-sm'>
                <TrendingUp className='mr-1 h-4 w-4' />
                <span>{exam.enrolled?.toLocaleString() || '0'} lượt thi</span>
              </div>
            </div>

            <p className='text-sm text-muted-foreground line-clamp-2'>{exam.description}</p>
          </div>

          <div className='flex items-center justify-between pt-4 border-t border-border mt-4'>
            <div className='flex items-center gap-2 flex-wrap'>
              {isPaid && exam.price > 0 ? (
                <>
                  <span className='text-lg font-bold text-primary line-through opacity-70'>
                    {(exam.price || 0).toLocaleString()}đ
                  </span>
                  <Badge
                    variant='secondary'
                    className='bg-success/10 text-success border-success/30 px-3 py-1 font-semibold'
                  >
                    Đã thanh toán
                  </Badge>
                </>
              ) : exam.price === 0 ? (
                <span className='text-lg font-bold text-green-600 dark:text-green-500'>
                  Miễn phí
                </span>
              ) : (
                <span className='text-lg font-bold text-primary'>
                  {(exam.price || 0).toLocaleString()}đ
                </span>
              )}
            </div>

            <Button
              onClick={() => onSelect(exam)}
              className={`text-white ${exam.price === 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-primary hover:bg-orange-600'}`}
              size='sm'
              disabled={isCheckingPayment || !isExamReady}
            >
              {isCheckingPayment ? (
                <Loader2 className='mr-1 h-4 w-4 animate-spin' />
              ) : exam.price === 0 || isPaid ? (
                <Play className='mr-1 h-4 w-4' />
              ) : (
                <Lock className='mr-1 h-4 w-4' />
              )}
              {!isExamReady
                ? 'Sắp ra mắt'
                : exam.price === 0 || isPaid
                  ? 'Thi thử ngay'
                  : `Luyện thi  -  ${(exam.price || 0).toLocaleString()}đ`}
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function ExamLibraryPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedPaymentType, setSelectedPaymentType] = useState('all');
  const [selectedSort, setSelectedSort] = useState('popular');
  const [selectedExam, setSelectedExam] = useState<ExamInfo | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showStartConfirmation, setShowStartConfirmation] = useState(false);
  const [paidExamIds, setPaidExamIds] = useState<Set<string>>(new Set());
  const [checkingExamId, setCheckingExamId] = useState<string | null>(null);

  // Show all states
  const [showAllPremium, setShowAllPremium] = useState(false);
  const [showAllFree, setShowAllFree] = useState(false);
  const [showAllBGD, setShowAllBGD] = useState(false);

  // Track scroll depth
  useEffect(() => {
    return initScrollDepthTracking();
  }, []);

  // Track search with debounce
  useEffect(() => {
    if (!searchTerm) return;
    const timer = setTimeout(() => {
      trackExamSearch({ search_term: searchTerm });
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Track filter changes (skip initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (selectedSubject !== 'all')
      trackExamFilter({ filter_type: 'subject', filter_value: selectedSubject });
  }, [selectedSubject]);

  useEffect(() => {
    if (selectedLevel !== 'all')
      trackExamFilter({ filter_type: 'level', filter_value: selectedLevel });
  }, [selectedLevel]);

  useEffect(() => {
    if (selectedPaymentType !== 'all')
      trackExamFilter({ filter_type: 'payment_type', filter_value: selectedPaymentType });
  }, [selectedPaymentType]);

  useEffect(() => {
    if (selectedSort !== 'popular')
      trackExamFilter({ filter_type: 'sort', filter_value: selectedSort });
  }, [selectedSort]);

  // Fetch exams from API (filtered by systemContext = THPT or BOTH via X-System-Context header)
  // Don't filter by standard - we want to show all exams regardless of standard (VSAT/THPT format)
  // The backend will automatically filter by systemContext based on the app's context
  const initialParams = useMemo(() => ({ status: 'ACTIVE' }), []);
  const { exams: apiExams, loading, error } = useExams({ autoFetch: true, initialParams });

  // Use ONLY API exams
  const exams = apiExams;

  // Get unique subjects for filter
  const subjects = useMemo(() => {
    const uniqueSubjects = [...new Set(exams.map(exam => exam.subject))];
    return uniqueSubjects;
  }, [exams]);

  // Filter and sort logic
  const filteredExams = useMemo(() => {
    let filtered = exams.filter(exam => {
      const isExamReady = exam.status === 'ACTIVE';
      const matchesSearch =
        searchTerm === '' ||
        exam.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exam.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exam.topics?.some(topic => topic.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesSubject = selectedSubject === 'all' || exam.subject === selectedSubject;
      const matchesLevel = selectedLevel === 'all' || exam.level === selectedLevel;
      const matchesPayment =
        selectedPaymentType === 'all' ||
        (selectedPaymentType === 'paid' && exam.price > 0) ||
        (selectedPaymentType === 'free' && exam.price === 0);

      return isExamReady && matchesSearch && matchesSubject && matchesLevel && matchesPayment;
    });

    // Sort by selected criteria
    filtered = filtered.sort((a, b) => {
      if (selectedSort === 'newest') {
        const dateA = (a as any).updatedAt ? new Date((a as any).updatedAt).getTime() : 0;
        const dateB = (b as any).updatedAt ? new Date((b as any).updatedAt).getTime() : 0;
        return dateB - dateA;
      } else if (selectedSort === 'rate') {
        return (b.rating ?? 0) - (a.rating ?? 0);
      }
      // Default 'popular': sort by enrolled (desc)
      return (b.enrolled ?? 0) - (a.enrolled ?? 0);
    });

    return filtered;
  }, [exams, searchTerm, selectedSubject, selectedLevel, selectedPaymentType, selectedSort]);

  // Categorize exams
  const bgdExams = filteredExams.filter(exam => exam.type === 'bgd');
  const premiumExams = filteredExams.filter(exam => exam.type !== 'bgd' && (exam.price ?? 0) > 0);
  const freeExams = filteredExams.filter(exam => exam.type !== 'bgd' && (exam.price ?? 0) === 0);

  const handleExamSelect = useCallback(
    async (exam: ExamInfo) => {
      if (exam.status !== 'ACTIVE') {
        toast.error('Đề thi chưa sẵn sàng');
        return;
      }

      // Navigate to exam detail page (public)
      navigate(`/thi-thu-thpt/${exam.id}`);
    },
    [navigate]
  );

  const handleStartExam = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Vui lòng đăng nhập để bắt đầu thi');
      navigate('/login');
      return;
    }

    if (selectedExam) {
      try {
        const sessionResponse = await apiService.startExamSession(selectedExam.id);
        const questionsResponse = await apiService.getExamQuestions(selectedExam.id);

        localStorage.setItem('currentExamSession', JSON.stringify(sessionResponse));
        localStorage.setItem('currentExamQuestions', JSON.stringify(questionsResponse));

        trackExamStart({
          exam_id: selectedExam.id,
          exam_name: selectedExam.title,
          subject: selectedExam.subject,
          is_premium: selectedExam.price > 0,
        });
        navigate(`/lam-bai/${selectedExam.id}`);
      } catch (error) {
        const status = (error as any)?.response?.status;
        const errorMessage = (error as any)?.response?.data?.message || '';

        if (status === 403 && errorMessage.includes('Payment already consumed')) {
          if (selectedExam?.id) {
            setPaidExamIds(prev => {
              const next = new Set(prev);
              next.delete(selectedExam.id);
              return next;
            });
          }
          toast.error('Bạn cần mở khóa lại để làm bài thi này.');
          setShowStartConfirmation(false);
          if (selectedExam && selectedExam.price > 0) {
            setShowPaymentDialog(true);
          }
          return;
        }

        toast.error(errorMessage || 'Không thể bắt đầu bài thi. Vui lòng thử lại.');
        setShowStartConfirmation(false);
      }
    }
  }, [selectedExam, isAuthenticated, navigate]);

  const handlePaymentSuccess = useCallback(() => {
    setShowPaymentDialog(false);
    setShowStartConfirmation(true);
    if (selectedExam?.id) {
      setPaidExamIds(prev => {
        const next = new Set(prev);
        next.add(selectedExam.id);
        return next;
      });
    }
    if (selectedExam) {
      trackExamPurchase({
        exam_id: selectedExam.id,
        exam_name: selectedExam.title,
        value: selectedExam.price,
        currency: 'VND',
      });
    }
    toast.success('Mở khóa thành công!');
  }, [selectedExam]);

  // Error state
  if (error) {
    return (
      <div className='min-h-screen bg-background-light dark:bg-background-dark flex flex-col'>
        <UnifiedHeader />
        <main className='flex-1 pt-20 lg:pt-24'>
          <div className='container mx-auto px-4 py-6'>
            <div className='flex items-center justify-center min-h-[400px]'>
              <div className='text-center'>
                <AlertCircle className='h-16 w-16 text-red-500 mx-auto mb-4' />
                <h2 className='text-2xl font-bold text-foreground dark:text-white mb-2'>
                  Lỗi tải dữ liệu
                </h2>
                <p className='text-muted-foreground mb-4'>{error}</p>
                <Button onClick={() => window.location.reload()} variant='outline'>
                  Thử lại
                </Button>
              </div>
            </div>
          </div>
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-background-light dark:bg-background-dark flex flex-col'>
      <SEOHead
        title='Kho đề thi THPT | Thi thử TN THPT Online'
        description='Luyện tập với hàng trăm đề thi thử THPT quốc gia online. Đề miễn phí & Premium chỉ từ 15.000đ. Chấm điểm tức thì, phân tích chi tiết.'
        keywords='đề thi thử THPT, thi thử THPT online, thi thử THPT quốc gia, luyện thi THPT, đề thi THPT 2026'
        canonicalUrl={getCanonicalUrl('/thi-thu-thpt')}
      />

      {/* ItemList Schema for exam library */}
      {filteredExams.length > 0 && (
        <StructuredData
          data={{
            type: 'ItemList',
            name: 'Kho đề thi THPT',
            description: 'Danh sách đề thi thử THPT quốc gia online',
            url: getCanonicalUrl('/thi-thu-thpt'),
            items: filteredExams.slice(0, 20).map(exam => ({
              name: exam.title.normalize('NFD'),
              url: getCanonicalUrl(`/thi-thu-thpt/${exam.id}`),
              description: exam.description,
              price: exam.price,
            })),
          }}
        />
      )}

      {/* BreadcrumbList Schema */}
      <StructuredData
        data={{
          type: 'BreadcrumbList',
          items: [
            { name: 'Trang chủ', url: getSiteUrl() },
            { name: 'Kho đề thi', url: getCanonicalUrl('/thi-thu-thpt') },
          ],
        }}
      />

      <UnifiedHeader />

      {/* Breadcrumbs */}
      <div className='pt-20 lg:pt-24'>
        <Breadcrumbs items={[{ label: 'Trang chủ', href: '/' }, { label: 'Kho đề thi' }]} />
      </div>

      <main className='flex-1 pb-12'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
          {/* Header with Career CTA */}
          <div className='mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4'>
            <div className='flex-1'>
              <h1 className='text-3xl font-bold text-gray-900 dark:text-white mb-2'>
                {PAGE_TITLE}
              </h1>
              <p className='text-gray-500 dark:text-gray-400'>{PAGE_DESCRIPTION}</p>
              <div className='mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-amber-500/10 border border-primary/20'>
                <Sparkles className='w-4 h-4 text-primary' />
                <span className='text-sm font-semibold text-primary'>
                  Đề miễn phí & Premium chỉ từ 15.000đ/đề - Rẻ hơn 1 ly trà sữa!
                </span>
              </div>
            </div>

            {/* Desktop View: Interactive Hover Component */}
            <div className='group relative flex-shrink-0 w-full lg:w-auto z-50'>
              {/* Compact View */}
              <div className='flex items-center gap-3 px-4 py-3 rounded-lg border border-primary/30 group-hover:border-primary/50 bg-primary/5 group-hover:opacity-0 transition-all duration-300 cursor-pointer w-full lg:w-auto overflow-hidden'>
                <div className='p-1.5 rounded-lg bg-primary/10'>
                  <Target className='w-5 h-5 text-primary' />
                </div>
                <div className='text-left'>
                  <div className='font-semibold text-gray-900 dark:text-white text-sm truncate'>
                    Đánh giá năng lực linh hoạt
                  </div>
                  <div className='text-xs text-gray-500 dark:text-gray-400'>
                    <span className='lg:hidden'>Chạm để xem chi tiết</span>
                    <span className='hidden lg:inline'>Hover để xem chi tiết</span>
                  </div>
                </div>
                <ArrowRight className='w-4 h-4 text-primary ml-1' />
              </div>

              {/* Expanded View on Hover (Desktop Only) */}
              <div className='absolute right-0 top-0 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-300 w-full lg:w-96 pt-0'>
                <div className='p-5 rounded-xl border-2 border-primary/30 bg-white dark:bg-gray-900 shadow-xl relative'>
                  {/* Invisible bridge to prevent hover loss */}
                  <div className='absolute -inset-4 -z-10' />

                  <div className='flex items-start gap-3 mb-3'>
                    <div className='p-2 rounded-lg bg-primary/10'>
                      <Target className='w-5 h-5 text-primary' />
                    </div>
                    <div>
                      <h3 className='font-bold text-gray-900 dark:text-white mb-2'>
                        Đánh giá năng lực linh hoạt
                      </h3>
                      <div className='text-sm text-gray-600 dark:text-gray-300 leading-relaxed text-left'>
                        <p className='mb-2'>Học sinh lựa chọn làm bài thi thử theo nhu cầu.</p>
                        <p className='mb-2'>
                          Học sinh sẽ nhận được Hồ sơ năng lực chi tiết phản ánh đúng sở trường và
                          năng lực cá nhân, hỗ trợ định hướng học tập và xét tuyển hiệu quả khi đáp
                          ứng đủ các điều kiện sau:
                        </p>
                        <ul className='list-disc list-inside space-y-1 ml-1'>
                          <li>
                            Hoàn thành tối thiểu 3 lượt thi thử cho 1 môn hoặc 1 lượt thi thử cho 3
                            môn bất kỳ;
                          </li>
                          <li>Hoàn thành bài tự đánh giá Hướng nghiệp CAT.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className='flex flex-col sm:flex-row gap-2'>
                    <Link
                      to='/huong-nghiep-cat'
                      className='flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-medium transition-colors'
                    >
                      <BookOpenIcon className='w-4 h-4' />
                      <span>Khám phá ngay</span>
                    </Link>
                    <Link
                      to='/gioi-thieu'
                      className='flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium transition-colors'
                    >
                      <span>Tìm hiểu thêm</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className='mb-8'>
            <div className='flex flex-col lg:flex-row gap-4'>
              <div className='relative w-full lg:flex-1'>
                <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground' />
                <Input
                  type='text'
                  placeholder='Tìm kiếm đề thi...'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className='pl-10'
                />
              </div>

              <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                <SelectTrigger className='w-full lg:w-[160px]'>
                  <SelectValue placeholder='Môn học' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>Tất cả môn học</SelectItem>
                  {subjects.map(subject => (
                    <SelectItem key={subject} value={subject}>
                      {subject}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                <SelectTrigger className='w-full lg:w-[140px]'>
                  <SelectValue placeholder='Độ khó' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>Tất cả mức độ</SelectItem>
                  <SelectItem value='basic'>Cơ bản</SelectItem>
                  <SelectItem value='intermediate'>Trung bình</SelectItem>
                  <SelectItem value='advanced'>Nâng cao</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedPaymentType} onValueChange={setSelectedPaymentType}>
                <SelectTrigger className='w-full lg:w-[140px]'>
                  <SelectValue placeholder='Loại đề' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>Tất cả</SelectItem>
                  <SelectItem value='free'>Miễn phí</SelectItem>
                  <SelectItem value='paid'>Trả phí</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedSort} onValueChange={setSelectedSort}>
                <SelectTrigger className='w-full lg:w-[160px]'>
                  <SelectValue placeholder='Sắp xếp' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='popular'>Phổ biến nhất</SelectItem>
                  <SelectItem value='newest'>Mới nhất</SelectItem>
                  <SelectItem value='rate'>Đánh giá cao</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Loading state */}
          {loading && (
            <div className='flex items-center justify-center py-12'>
              <Loader2 className='h-8 w-8 animate-spin text-primary' />
            </div>
          )}

          {/* Live Activity Social Proof */}
          <LiveActivityWidget onExamClick={examId => navigate(`/thi-thu-thpt/${examId}`)} />

          {/* Exam sections */}
          {!loading && (
            <div>
              {bgdExams.length > 0 && (
                <ExamSection
                  title='Đề thi chính thức BGD'
                  description='Các đề thi chính thức từ Bộ Giáo dục và Đào tạo'
                  icon={<Crown className='h-6 w-6 text-blue-600' />}
                  badge={
                    <Badge className='bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800'>
                      Chính thức
                    </Badge>
                  }
                  exams={bgdExams}
                  onExamSelect={handleExamSelect}
                  showAll={showAllBGD}
                  onToggleShowAll={() => setShowAllBGD(!showAllBGD)}
                  isPaid={id => paidExamIds.has(id)}
                  isCheckingPayment={id => checkingExamId === id}
                />
              )}

              {premiumExams.length > 0 && (
                <ExamSection
                  title='Đề thi Premium'
                  description='Đề thi được rút ngẫu nhiên theo định dạng của ma trận thi tốt nghiệp THPT. Mỗi lần thi sẽ có các câu hỏi khác nhau không trùng lặp, giúp học sinh luyện tập đa dạng và hiệu quả.'
                  icon={<Crown className='h-6 w-6 text-primary' />}
                  badge={
                    <Badge className='bg-primary/10 text-primary border-primary/40'>
                      <Sparkles className='mr-1 h-3 w-3' />
                      Premium
                    </Badge>
                  }
                  exams={premiumExams}
                  onExamSelect={handleExamSelect}
                  showAll={showAllPremium}
                  onToggleShowAll={() => setShowAllPremium(!showAllPremium)}
                  isPaid={id => paidExamIds.has(id)}
                  isCheckingPayment={id => checkingExamId === id}
                />
              )}

              {freeExams.length > 0 && (
                <ExamSection
                  title='Đề thi miễn phí'
                  description='Luyện tập không giới hạn với các đề thi miễn phí'
                  icon={<Gift className='h-6 w-6 text-success' />}
                  badge={
                    <Badge className='bg-success/10 text-success border-success/40'>Miễn phí</Badge>
                  }
                  exams={freeExams}
                  onExamSelect={handleExamSelect}
                  showAll={showAllFree}
                  onToggleShowAll={() => setShowAllFree(!showAllFree)}
                  isPaid={id => paidExamIds.has(id)}
                  isCheckingPayment={id => checkingExamId === id}
                />
              )}

              {filteredExams.length === 0 && (
                <div className='text-center py-12'>
                  <p className='text-muted-foreground'>Không tìm thấy đề thi phù hợp</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <UnifiedFooter />

      {/* Payment Dialog */}
      {selectedExam && (
        <ModernPaymentDialog
          open={showPaymentDialog}
          onOpenChange={setShowPaymentDialog}
          examTitle={selectedExam.title}
          examTitleVi={selectedExam.title}
          price={selectedExam.price}
          examId={selectedExam.id}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}

      {/* Exam Start Confirmation */}
      {selectedExam && (
        <ExamStartConfirmation
          open={showStartConfirmation}
          onOpenChange={setShowStartConfirmation}
          examTitle={selectedExam.title}
          examTitleVi={selectedExam.title}
          duration={selectedExam.duration}
          questions={selectedExam.totalQuestions}
          isPaid={selectedExam.price > 0}
          onConfirm={handleStartExam}
        />
      )}
    </div>
  );
}
